// components/CreateChecklistTemplateModal.tsx
import React, { useState } from "react";
import { Button, Modal, Text, Stack, Code, LoadingOverlay, TextInput, Group, Divider, Paper } from "@mantine/core";
import { useForm } from "@mantine/form";
import { usePostChecklistMcTemplatesMutation } from "../../openapi";

interface FormValues {
  title: string;
}

const CreateChecklistTemplateModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [postTemplate, { isLoading, data, error, reset }] = usePostChecklistMcTemplatesMutation();

  const form = useForm<FormValues>({
    initialValues: { title: "" },
    validate: {
      title: (value) => (value.trim().length < 1 ? "Tittel kreves" : null),
    },
  });

  const handleSubmit = async () => {
    const errors = form.validate();
    if (errors.hasErrors) return;

    await postTemplate({ body: { title: form.values.title.trim() } });
  };

  React.useEffect(() => {
    if (data) {
      // Låt utvecklare granska JSON – stäng inte automatiskt
    }
  }, [data]);

  return (
    <Modal opened onClose={onClose} title="Opprett ny mal" size="lg">
      <Stack>
        <Text size="sm" c="dimmed">
          Opprett en ny mal med tittel, og få JSON + Excel som svar.
        </Text>

        <Divider />

        <form onSubmit={form.onSubmit(handleSubmit)}>
          <TextInput
            label="Tittel på mal"
            placeholder="F.eks. Ny standard for el-kontroll"
            required
            {...form.getInputProps("title")}
          />

          <Group justify="flex-end" mt="md">
            <Button type="submit" loading={isLoading} color="green">
              Opprett mal
            </Button>
          </Group>
        </form>

        {isLoading && <LoadingOverlay visible />}

        {error && (
          <Paper withBorder p="sm" radius="md" style={{ backgroundColor: "#ffcccc" }}>
            <Text fw={500} c="red">
              Feil ved opprettelse:
            </Text>
            <Code block>{JSON.stringify(error, null, 2)}</Code>
          </Paper>
        )}

        {data && (
          <Stack>
            <Paper withBorder p="sm" radius="md" style={{ backgroundColor: "#ccffcc" }}>
              <Text fw={500} c="green">
                Mal opprettet! ID: {data.checklistId}
              </Text>
            </Paper>

            <Divider label="Respons (JSON)" />

            <Paper withBorder p="sm" radius="md">
              <Code block style={{ maxHeight: 400, overflow: "auto" }}>
                {JSON.stringify(data, null, 2)}
              </Code>
            </Paper>

            <Divider label="Excel-fil" />

            <Paper withBorder p="sm" radius="md">
              <Group align="center">
                <Text size="sm">
                  <strong>Filnavn:</strong> {data.excel.filename}
                </Text>
                <Button
                  size="xs"
                  variant="light"
                  onClick={() => {
                    const link = document.createElement("a");
                    link.href = `data:${data.excel.contentType};base64,${data.excel.base64}`;
                    link.download = data.excel.filename;
                    link.click();
                  }}
                >
                  Last ned Excel
                </Button>
              </Group>
            </Paper>

            <Divider label="Utviklerinfo" />

            <Paper withBorder p="sm" radius="md">
              <Stack gap="xs">
                <Group gap="xs">
                  <Code>checklistId</Code>
                  <Text>{data.checklistId}</Text>
                </Group>
                <Group gap="xs">
                  <Code>status</Code>
                  <Text>{data.status}</Text>
                </Group>
                <Group gap="xs">
                  <Code>items.length</Code>
                  <Text>{data.items.length}</Text>
                </Group>
              </Stack>
            </Paper>
          </Stack>
        )}
      </Stack>
    </Modal>
  );
};

export const CreateChecklistTemplateButton: React.FC = () => {
  const [opened, setOpened] = useState(false);

  return (
    <>
      <Button
        style={{ width: "200px" }}
        onClick={() => setOpened(true)}
        leftSection={<i className="fa fa-plus" />}
        color="green"
        variant="filled"
      >
        Opprett ny mal
      </Button>

      {opened && <CreateChecklistTemplateModal onClose={() => setOpened(false)} />}
    </>
  );
};

export default CreateChecklistTemplateButton;
